import {Keyword} from "./lexer";
import {
	CompilationUnitTree,
	FunctionDeclarationTree,
	PrimitiveTypeTree,
} from "./model";
import {patternMatch} from "./pattern_match";
import {
	getFirstScopeOfChain,
	HoverPattern,
	HoverPatternFrom,
} from "./hovers/utils";
import {LITERALS_HOVERS} from "./hovers/literals";
import {MODIFIERS_HOVERS} from "./hovers/modifiers";
import {CONTROL_FLOW_HOVERS} from "./hovers/control_flow";
import {TYPES_HOVERS} from "./hovers/types";
import {HoverResult} from "./hovers/hover_result";

export class HoverEngine {
	patterns: HoverPattern<any, any>[] = [];

	constructor() {
		const voidFnReturnType = [
			FunctionDeclarationTree,
			PrimitiveTypeTree,
			Keyword.void,
		] as const;
		const void_: HoverPatternFrom<typeof voidFnReturnType> = {
			pat: voidFnReturnType,
			handler: (_chain, scope, a, b, c) => {
				return `The return type of this function, <code>${a.name.value}</code>, is <code>void</code>. This means that the function does not return a value.`;
			},
		};

		this.registerPattern(void_);
		this.registerPatterns(MODIFIERS_HOVERS);
		this.registerPatterns(LITERALS_HOVERS);
		this.registerPatterns(CONTROL_FLOW_HOVERS);
		this.registerPatterns(TYPES_HOVERS);
	}

	/**
	 * Register multiple patterns to be used for hover information.
	 */
	registerPatterns(patterns: HoverPattern<any, any>[]) {
		for (const pat of patterns) this.registerPattern(pat);
	}

	/**
	 * Register a pattern to be used for hover information.
	 */
	registerPattern(pattern: HoverPattern<any, any>) {
		this.patterns.push(pattern);
	}

	hoverAt(compilationUnit: CompilationUnitTree, pos: number): HoverResult[] {
		const chain = compilationUnit.findChainAtPosition(pos);
		if (!chain) return [];
		const scope = getFirstScopeOfChain(chain);
		if (!scope) return [];

		const results: HoverResult[] = [];
		for (const pat of this.patterns) {
			const matched = patternMatch(pat.pat, chain);
			if (matched) {
				const result = pat.handler(chain, scope, ...matched);
				if (typeof result === "string") {
					results.push(HoverResult.string(result));
				} else if (result) {
					results.push(result);
				}
			}
		}

		return results;
	}
}
